import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Table, Td, inputCls, ghostBtnCls, primaryBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { dmyTime, titleize } from "@/lib/format";

const EMPTY_FILTERS = { actor: "", action: "", shift_id: "", from_date: "", to_date: "" };

export default function AuditLog() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [shifts, setShifts] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const load = useCallback(async (f) => {
    setItems(null);
    const params = { limit: 300 };
    Object.entries(f || {}).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/audit-logs", { params });
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => {
    if (!can("audit.view")) return;
    load(EMPTY_FILTERS);
    if (can("staff.shifts.view")) {
      api.get("/shifts").then(({ data }) => setShifts(data.items)).catch(() => {});
    }
  }, [can, load]);

  if (!can("audit.view")) return <NoAccess what="the audit log" />;
  if (error) return <ErrorState message={error} />;

  const apply = () => load(filters);
  const reset = () => { setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS); };

  return (
    <div data-testid="audit-page">
      <PageHeader overline="Administration" title="Audit log"
        subtitle="Immutable record of sensitive actions — with the staff member and their working shift at the time of each action. Filter by staff, module, shift and date." />

      <div className="panel mb-6 p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-6 items-end" data-testid="audit-filters">
        <label className="text-xs text-slate-500 lg:col-span-2">Staff (email)
          <input className={inputCls} data-testid="audit-filter-actor" value={filters.actor}
            placeholder="name@business…" onChange={(e) => setFilters({ ...filters, actor: e.target.value })} /></label>
        <label className="text-xs text-slate-500">Action / module
          <input className={inputCls} data-testid="audit-filter-action" value={filters.action}
            placeholder="e.g. invoice, opd, lab" onChange={(e) => setFilters({ ...filters, action: e.target.value })} /></label>
        <label className="text-xs text-slate-500">Shift
          <select className={inputCls} data-testid="audit-filter-shift" value={filters.shift_id}
            onChange={(e) => setFilters({ ...filters, shift_id: e.target.value })}>
            <option value="">All shifts</option>
            {shifts.map((s) => <option key={s.shift_id} value={s.shift_id}>{s.name}</option>)}
          </select></label>
        <label className="text-xs text-slate-500">From
          <input type="date" className={inputCls} data-testid="audit-filter-from" value={filters.from_date}
            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} /></label>
        <label className="text-xs text-slate-500">To
          <input type="date" className={inputCls} data-testid="audit-filter-to" value={filters.to_date}
            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} /></label>
        <div className="flex gap-2 lg:col-span-6">
          <button onClick={apply} className={primaryBtnCls} data-testid="audit-apply-btn">Apply filters</button>
          <button onClick={reset} className={ghostBtnCls} data-testid="audit-reset-btn">Reset</button>
        </div>
      </div>

      {!items ? <Loading /> : (
        <Table testid="audit-table" columns={["When", "Action", "Actor", "Shift", "Entity", "Result"]}
          rows={items} empty={<EmptyState title="No audit events match" testid="audit-empty" />}
          renderRow={(a) => (
            <tr key={a.log_id} data-testid={`audit-row-${a.log_id}`}>
              <Td className="text-slate-500 whitespace-nowrap">{dmyTime(a.created_at)}</Td>
              <Td className="font-medium text-slate-900">{titleize(a.action)}</Td>
              <Td className="text-slate-600">{a.user_name || a.user_email || "system"}
                {a.user_role && <span className="block text-xs text-slate-400">{titleize(a.user_role)}</span>}</Td>
              <Td className="text-slate-500">{a.shift_name || "—"}</Td>
              <Td className="text-slate-500">{a.entity || "—"}</Td>
              <Td><StatusPill status={a.result} /></Td>
            </tr>
          )} />
      )}
    </div>
  );
}
